import type { HomeClient, RevenueSnapshot } from "@acto/ambient-context-schema";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { capList, toIsoDate, toTimeframe, type PeriodSelectionInput } from "./helpers";

export interface CustomersToClientsOptions {
  /** False (default) sends ids only; true adds the card's rendered values. */
  includeDetails?: boolean;
  /** Locale for the card's localized short story; without it details mode omits `signal`. */
  locale?: string;
  /** Backend-resolved KPI window the cards' revenue figures cover (rankedClients.dateRanges.current). */
  revenuePeriod?: { start: Date | string; end: Date | string } | null;
  limit?: number;
}

/** A rendered customer list → schema clients. Ids-only unless the caller opts into details. */
export function customersToClients(
  customers: Customer[] | undefined,
  { includeDetails = false, locale, revenuePeriod, limit }: CustomersToClientsOptions = {},
): { clients: HomeClient[] } {
  return {
    clients: capList(customers ?? [], limit).map((c) =>
      includeDetails
        ? {
            external_reference: c.externalReference,
            name: c.name,
            revenue: c.revenueKpi
              ? {
                  total_value: c.revenueKpi.totalValue,
                  previous_total_value: c.revenueKpi.previousTotalValue,
                  percentage_change: c.revenueKpi.percentageChange ?? undefined,
                  timeframe_start: revenuePeriod ? toIsoDate(revenuePeriod.start) : undefined,
                  timeframe_end: revenuePeriod ? toIsoDate(revenuePeriod.end) : undefined,
                }
              : undefined,
            signal: locale ? c.getLocalizedShortStory(locale) : undefined,
            last_visit_date: c.lastVisitDate,
            last_order_date: c.lastOrder?.date,
          }
        : { external_reference: c.externalReference },
    ),
  };
}

/** Structural mirror of the fs2 KpiTrendRaw, so core mappers don't import fs2 modules. */
export interface KpiTrendRawInput {
  totalValue: number;
  previousTotalValue?: number;
  percentageChange?: number | null;
  series: { periodStart: string; value: number; previousValue?: number }[];
}

/**
 * A resolved KPI trend + its period selection → the schema's RevenueSnapshot.
 * The `revenue` key is always present — `undefined` while the trend is unresolved —
 * so a remount on the same path overwrites any previously contributed value
 * instead of leaving it stale (contribute() merges by key and never clears absent ones).
 */
export function trendToRevenueSnapshot(
  raw: KpiTrendRawInput | undefined,
  selection: PeriodSelectionInput | undefined,
  { includeSeries = true }: { includeSeries?: boolean } = {},
): { revenue: RevenueSnapshot | undefined } {
  if (!raw) return { revenue: undefined };
  return {
    revenue: {
      total_value: raw.totalValue,
      previous_total_value: raw.previousTotalValue,
      percentage_change: raw.percentageChange,
      ...toTimeframe(selection),
      monthly_series: includeSeries
        ? raw.series.map((point) => ({
            period_start: toIsoDate(point.periodStart) ?? point.periodStart,
            value: point.value,
            previous_value: point.previousValue,
          }))
        : undefined,
    },
  };
}
